/**
 * Admin Feature - Custom Hooks
 *
 * Hooks reutilizáveis para o módulo admin
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import useAdminStore from './admin.store'
import {
	metricsApi,
	usersApi,
	clubsApi,
	matchesApi,
	feedbacksApi,
	broadcastApi,
	logsApi,
} from './admin.api'

// =====================================================
// AUTH HOOKS
// =====================================================

/**
 * Hook para autenticação do admin
 */
export function useAdminAuth() {
	const navigate = useNavigate()
	const {
		admin,
		isAuthenticated,
		isReady,
		loading,
		error,
		login,
		logout,
		initializeAuth,
		clearError,
	} = useAdminStore()

	const handleLogin = async (email, password) => {
		const result = await login(email, password)
		// Navegação removida - deixar o useEffect do LoginScreen fazer o redirect
		return result
	}

	const handleLogout = () => {
		logout()
		navigate('/admin/login')
	}

	return {
		admin,
		isAuthenticated,
		isReady,
		loading,
		error,
		login: handleLogin,
		logout: handleLogout,
		initializeAuth,
		clearError,
	}
}

/**
 * Hook para proteger rotas admin
 */
export function useAdminGuard() {
	const navigate = useNavigate()
	const { isAuthenticated, isReady, initializeAuth } = useAdminStore()

	useEffect(() => {
		const init = async () => {
			// Inicializa a autenticação (carrega token do localStorage)
			const valid = await initializeAuth()
			
			if (!valid) {
				navigate('/admin/login')
			}
		}
		
		// Só inicializa se ainda não estiver pronto
		if (!isReady) {
			init()
		}
	}, [initializeAuth, navigate, isReady])

	return { isAuthenticated, checking: !isReady }
}

// =====================================================
// DATA HOOKS
// =====================================================

/**
 * Hook genérico para fetch de dados com loading/error
 */
function useDataFetch(fetchFn, dependencies = []) {
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const fetch = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const result = await fetchFn()
			setData(result)
		} catch (err) {
			setError(err.response?.data?.error || 'Erro ao carregar dados')
		} finally {
			setLoading(false)
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, dependencies)

	useEffect(() => {
		fetch()
	}, [fetch])

	return { data, loading, error, refetch: fetch }
}

/**
 * Hook para métricas do dashboard
 */
export function useDashboardMetrics() {
	const [overview, setOverview] = useState(null)
	const [growth, setGrowth] = useState(null)
	const [activity, setActivity] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const fetchAll = useCallback(async () => {
		setLoading(true)
		setError(null)

		try {
			const [overviewData, growthData, activityData] = await Promise.all([
				metricsApi.getOverview(),
				metricsApi.getGrowthData(30),
				metricsApi.getRecentActivity(10),
			])

			setOverview(overviewData)
			setGrowth(growthData)
			setActivity(activityData)
		} catch (err) {
			setError(err.response?.data?.error || 'Erro ao carregar métricas')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchAll()
	}, [fetchAll])

	return { overview, growth, activity, loading, error, refetch: fetchAll }
}

/**
 * Hook para lista paginada
 */
export function usePaginatedList(apiFn, initialParams = {}) {
	const [data, setData] = useState(null)
	const [page, setPage] = useState(1)
	const [limit, setLimit] = useState(initialParams.limit || 20)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [filters, setFilters] = useState(initialParams)
	const [search, setSearch] = useState('')

	const fetch = useCallback(async () => {
		setLoading(true)
		setError(null)

		try {
			const params = { ...filters, page, limit }
			if (search) params.search = search

			const result = await apiFn(params)
			// A API retorna { data: [...], pagination: {...} }
			setData(result)
		} catch (err) {
			setError(err.response?.data?.error || 'Erro ao carregar lista')
		} finally {
			setLoading(false)
		}
	}, [apiFn, filters, page, limit, search])

	useEffect(() => {
		fetch()
	}, [fetch])

	const updateFilters = (newFilters) => {
		setFilters((prev) => ({ ...prev, ...newFilters }))
		setPage(1) // Reset page quando filtros mudam
	}

	return {
		data,
		loading,
		error,
		page,
		limit,
		setPage,
		setLimit,
		setSearch,
		filters,
		updateFilters,
		refetch: fetch,
	}
}

/**
 * Hook para usuários
 */
export function useUsers(initialParams = {}) {
	return usePaginatedList(usersApi.list, initialParams)
}

/**
 * Hook para clubes
 */
export function useClubs(initialParams = {}) {
	return usePaginatedList(clubsApi.list, initialParams)
}

/**
 * Hook para partidas
 */
export function useMatches(initialParams = {}) {
	return usePaginatedList(matchesApi.list, initialParams)
}

/**
 * Hook para feedbacks
 */
export function useFeedbacks(initialParams = {}) {
	return usePaginatedList(feedbacksApi.list, initialParams)
}

/**
 * Hook para broadcast
 */
export function useBroadcast() {
	const [preview, setPreview] = useState(null)
	const [sending, setSending] = useState(false)
	const [error, setError] = useState(null)
	const [history, setHistory] = useState([])
	const [historyLoading, setHistoryLoading] = useState(true)

	const fetchHistory = useCallback(async () => {
		setHistoryLoading(true)
		try {
			const result = await broadcastApi.getHistory()
			// A API retorna { data: [...], pagination: {...} }
			setHistory(result?.data || [])
		} catch (err) {
			setError(err.response?.data?.error || 'Erro ao carregar histórico')
		} finally {
			setHistoryLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchHistory()
	}, [fetchHistory])

	const getPreview = async (data) => {
		try {
			const result = await broadcastApi.getPreview(data)
			setPreview(result)
			return result
		} catch (err) {
			setError(err.response?.data?.error || 'Erro ao gerar preview')
			return null
		}
	}

	const send = async (data) => {
		setSending(true)
		setError(null)

		try {
			const result = await broadcastApi.send(data)
			return { success: true, data: result }
		} catch (err) {
			const errorMsg = err.response?.data?.error || 'Erro ao enviar broadcast'
			setError(errorMsg)
			return { success: false, error: errorMsg }
		} finally {
			setSending(false)
		}
	}

	return {
		preview,
		sending,
		error,
		history,
		historyLoading,
		getPreview,
		send,
		refetchHistory: fetchHistory,
		clearError: () => setError(null),
	}
}

/**
 * Hook para logs de auditoria
 */
export function useAuditLogs(initialParams = {}) {
	return usePaginatedList(logsApi.list, initialParams)
}

/**
 * Hook para detalhes de um item
 */
export function useItemDetail(apiFn, id) {
	return useDataFetch(() => apiFn(id), [id])
}

const adminHooks = {
	useAdminAuth,
	useAdminGuard,
	useDashboardMetrics,
	useUsers,
	useClubs,
	useMatches,
	useFeedbacks,
	useBroadcast,
	useAuditLogs,
}

export default adminHooks
